import React, { forwardRef, useImperativeHandle, useRef, useState, useEffect } from 'react';

const Recorder = forwardRef(({ sendAudioUrlToParent, autoRec }, ref) => {
    const [isRecording, setIsRecording] = useState(false);
    const [hasPermission, setHasPermission] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [logs, setLogs] = useState([]);
    const mediaRecorder = useRef(null);
    const audioChunks = useRef([]);
    const isProcessing = useRef(false);
    const streamRef = useRef(null);
    const hasError = useRef(false);

    const addLog = (message) => {
        const timestamp = new Date().toLocaleTimeString();
        const newLog = `${timestamp}: ${message}`;
        console.log(newLog);
        setLogs(prevLogs => [newLog, ...prevLogs]);
    };

    const stopMediaTracks = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => {
                track.stop();
            });
            streamRef.current = null;
            mediaRecorder.current = null;
            addLog('Tracks de áudio paradas');
        }
    };

    useEffect(() => {
        addLog('=== Iniciando verificações do ambiente ===');
        const userAgent = navigator.userAgent;
        const iOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
        setIsIOS(iOS);
        addLog(`Dispositivo iOS: ${iOS}`);
        initializeRecorder();

        return () => {
            if (mediaRecorder.current?.state === 'recording') {
                try {
                    mediaRecorder.current.stop();
                } catch (err) {
                    addLog(`Erro ao parar gravação: ${err.message}`);
                }
            }
            stopMediaTracks();
        };
    }, []);

    const setupMediaRecorder = (stream) => {
        if (!stream) {
            addLog('Stream inválido ao configurar MediaRecorder');
            return false;
        }

        try {
            // Configurações específicas para iOS
            const options = {
                mimeType: isIOS ? 'audio/mp4' : 'audio/webm',
                audioBitsPerSecond: 128000
            };

            try {
                mediaRecorder.current = new MediaRecorder(stream, options);
            } catch (e) {
                // Fallback para configuração padrão se as opções específicas falharem
                addLog('Fallback para configuração padrão do MediaRecorder');
                mediaRecorder.current = new MediaRecorder(stream);
            }
            
            mediaRecorder.current.ondataavailable = (event) => {
                if (event.data.size > 0) {
                    audioChunks.current.push(event.data);
                }
            };

            mediaRecorder.current.onerror = (event) => {
                addLog(`Erro no MediaRecorder: ${event.error}`);
                hasError.current = true;
                stopMediaTracks();
            };

            mediaRecorder.current.onstop = async () => {
                try {
                    if (audioChunks.current.length > 0) {
                        // Tipo MIME específico para iOS
                        const mimeType = isIOS ? 'audio/mp4' : 'audio/webm';
                        const audioBlob = new Blob(audioChunks.current, { type: mimeType });
                        const audioUrl = URL.createObjectURL(audioBlob);
                        
                        // Para iOS, podemos precisar converter o formato
                        if (isIOS) {
                            try {
                                const audioElement = new Audio(audioUrl);
                                audioElement.load();
                                await new Promise((resolve) => {
                                    audioElement.oncanplaythrough = resolve;
                                    audioElement.onerror = resolve;
                                });
                            } catch (e) {
                                addLog(`Aviso: Pré-carregamento de áudio falhou: ${e.message}`);
                            }
                        }

                        await sendAudioUrlToParent(audioUrl);
                        addLog('Áudio processado com sucesso');
                    }
                } catch (error) {
                    addLog(`Erro ao processar áudio: ${error.message}`);
                    sendAudioUrlToParent(null);
                } finally {
                    audioChunks.current = [];
                    isProcessing.current = false;

                    if (autoRec && !hasError.current) {
                        setTimeout(() => {
                            if (!isProcessing.current && mediaRecorder.current) {
                                startNewRecording();
                            }
                        }, 1000);
                    }
                }
            };

            // Configuração do intervalo de dados para iOS
            if (isIOS) {
                mediaRecorder.current.timeslice = 1000; // 1 segundo
            }

            return true;
        } catch (error) {
            addLog(`Erro ao configurar MediaRecorder: ${error.message}`);
            return false;
        }
    };

    const startNewRecording = async () => {
        if (isProcessing.current || hasError.current) {
            addLog('Não é possível iniciar gravação: processando ou com erro');
            return;
        }

        try {
            if (!streamRef.current) {
                addLog('Stream não disponível, tentando reinicializar');
                await initializeRecorder();
                return;
            }

            if (mediaRecorder.current?.state === 'recording') {
                addLog('Já existe uma gravação em andamento');
                return;
            }

            if (!mediaRecorder.current) {
                const success = setupMediaRecorder(streamRef.current);
                if (!success) return;
            }

            audioChunks.current = [];
            
            // Para iOS, usamos timeslice para garantir chunks regulares
            if (isIOS) {
                mediaRecorder.current.start(1000);
            } else {
                mediaRecorder.current.start();
            }
            
            setIsRecording(true);
            addLog('Nova gravação iniciada');
        } catch (err) {
            addLog(`Erro ao iniciar gravação: ${err.message}`);
            setIsRecording(false);
            hasError.current = true;
            stopMediaTracks();
        }
    };

    const initializeRecorder = async () => {
        addLog('=== Tentando inicializar gravador ===');
        hasError.current = false;

        const constraints = {
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true
            }
        };

        try {
            if (streamRef.current) {
                addLog('Usando stream existente');
                return setupMediaRecorder(streamRef.current);
            }

            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            streamRef.current = stream;
            addLog('Novo stream de áudio obtido');
            
            const success = setupMediaRecorder(stream);
            if (success) {
                setHasPermission(true);
                addLog('Configuração completa!');

                if (autoRec) {
                    setTimeout(() => {
                        startNewRecording();
                    }, 1000);
                }
            }
        } catch (err) {
            addLog(`ERRO na inicialização: ${err.message}`);
            setHasPermission(false);
            hasError.current = true;
        }
    };

    useImperativeHandle(ref, () => ({
        startRecording: startNewRecording,
        stopRecording: () => {
            if (mediaRecorder.current?.state === 'recording') {
                try {
                    mediaRecorder.current.stop();
                    setIsRecording(false);
                    addLog('Gravação parada manualmente');
                    if (!autoRec) {
                        stopMediaTracks();
                    }
                } catch (err) {
                    addLog(`Erro ao parar gravação: ${err.message}`);
                    hasError.current = true;
                    stopMediaTracks();
                }
            }
        }
    }));

    return (
        <div style={{
            padding: '10px',
            backgroundColor: '#f5f5f5',
            margin: '10px'
        }}>
            {isIOS && !hasPermission && (
                <div style={{
                    marginTop: '10px',
                    padding: '10px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffeeba'
                }}>
                    <h4>Instruções para iOS:</h4>
                    <ol>
                        <li>Vá em Configurações</li>
                        <li>Safari</li>
                        <li>Permissões do site</li>
                        <li>Microfone</li>
                        <li>Ative para este site</li>
                    </ol>
                </div>
            )}

            {hasPermission && (
                <div className="recording-status">
                    <div className={`recording-indicator ${isRecording ? 'active' : ''}`}>
                        {isRecording ? 'Gravando...' : 'Pronto para gravar'}
                    </div>
                </div>
            )}
        </div>
    );
});

export default Recorder;